import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Card, CardContent, Grid, Button, MenuItem, Select, 
  FormControl, InputLabel, CircularProgress, Chip, IconButton, Alert,
  Divider, Stack, TextField, InputAdornment
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Search as SearchIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CLOUD_FUNCTION_BASE = 'https://us-central1-mylocalforce-295b8.cloudfunctions.net';

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const asFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCoords = (coords) => {
  if (!coords) return null;

  let lat = asFiniteNumber(coords.lat ?? coords.latitude ?? coords._lat);
  let lng = asFiniteNumber(
    coords.lng ??
    coords.longitude ??
    coords.lon ??
    coords.long ??
    coords._long ??
    coords._lng
  );

  if (lat === null || lng === null) return null;

  const latInRange = lat >= -90 && lat <= 90;
  const lngInRange = lng >= -180 && lng <= 180;
  const couldBeSwapped =
    (!latInRange && lng >= -90 && lng <= 90 && lat >= -180 && lat <= 180) ||
    (latInRange && lngInRange && Math.abs(lat) > 60 && Math.abs(lng) <= 60);

  if (couldBeSwapped) {
    const swappedLat = lng;
    const swappedLng = lat;
    lat = swappedLat;
    lng = swappedLng;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
};

const extractFromCoordinatePairString = (value) => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const plainPairMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!plainPairMatch) return null;

  const first = asFiniteNumber(plainPairMatch[1]);
  const second = asFiniteNumber(plainPairMatch[2]);
  if (first === null || second === null) return null;

  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return normalizeCoords({ lat: second, lng: first });
  }
  return normalizeCoords({ lat: first, lng: second });
};

const extractFromCoordinateArray = (value, isGeoJsonPoint = false) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  const first = asFiniteNumber(value[0]);
  const second = asFiniteNumber(value[1]);
  if (first === null || second === null) return null;

  if (isGeoJsonPoint || Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return normalizeCoords({ lat: second, lng: first });
  }

  return normalizeCoords({ lat: first, lng: second });
};

const extractCoords = (record = {}) => {
  if (!record || typeof record !== 'object') return null;

  const nestedKeys = [
    'location',
    'coords',
    'customerCoords',
    'providerLocation',
    'ownerLocation',
    'geopoint',
    'geoPoint',
    'addressLocation',
    'selectedAddress',
    'profile',
    'provider_onboarding',
    'details',
    'customerLocation',
    'serviceLocation',
    'currentLocation',
    'position',
    'coordinates',
    'geo',
  ];

  const visited = new Set();
  const queue = [record];

  while (queue.length > 0) {
    const candidate = queue.shift();
    if (candidate == null) continue;

    if (typeof candidate === 'string') {
      const fromString = extractFromCoordinatePairString(candidate);
      if (fromString) return fromString;
      continue;
    }

    if (Array.isArray(candidate)) {
      const fromArray = extractFromCoordinateArray(candidate);
      if (fromArray) return fromArray;

      for (const item of candidate) {
        if (item && (typeof item === 'object' || typeof item === 'string')) {
          queue.push(item);
        }
      }
      continue;
    }

    if (typeof candidate !== 'object' || visited.has(candidate)) {
      continue;
    }
    visited.add(candidate);

    const direct = normalizeCoords(candidate);
    if (direct) return direct;

    if (Array.isArray(candidate.coordinates)) {
      const fromCoordinates = extractFromCoordinateArray(
        candidate.coordinates,
        String(candidate.type || '').toLowerCase() === 'point'
      );
      if (fromCoordinates) return fromCoordinates;
    }

    for (const key of nestedKeys) {
      if (candidate[key] != null) {
        queue.push(candidate[key]);
      }
    }
  }

  return null;
};

const formatDistanceLabel = (distance) => (
  Number.isFinite(distance) ? `${distance.toFixed(1)} km away` : 'distance unavailable'
);

const normalizeText = (value) => String(value || '').toLowerCase().trim();

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const getProviderDisplayName = (provider = {}) => (
  provider.displayName ||
  provider.name ||
  provider.businessName ||
  provider.email ||
  'Unknown Provider'
);

const getProviderServiceDetails = (provider = {}) => {
  const combined = [
    ...normalizeList(provider.servicesOffered),
    ...normalizeList(provider.provider_onboarding?.servicesOffered),
    ...normalizeList(provider.profile?.servicesOffered),
    ...normalizeList(provider.serviceCategories),
    ...normalizeList(provider.serviceTypes),
    ...normalizeList(provider.skills),
    ...normalizeList(provider.categories),
  ];

  return Array.from(new Set(combined));
};

const getBookingServiceTerms = (booking = {}) => {
  const terms = [
    booking.serviceName,
    booking.subcategory,
    booking.leadSubcategory,
    booking.category,
    booking.leadCategory,
  ]
    .map((item) => String(item || '').trim())
    .filter(Boolean);

  return Array.from(new Set(terms));
};

const providerMatchesBookingService = (provider = {}, bookingTerms = []) => {
  if (!bookingTerms.length) return true;

  const providerServices = getProviderServiceDetails(provider).map(normalizeText);
  if (!providerServices.length) return false;

  const normalizedBookingTerms = bookingTerms.map(normalizeText);
  return normalizedBookingTerms.some((term) => (
    providerServices.some((service) => service.includes(term) || term.includes(service))
  ));
};

export default function AdminReassignBookings() {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [assigning, setAssigning] = useState({});
  const [selectedMap, setSelectedMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [maxDistance, setMaxDistance] = useState(50); // Default 50km radius

  const getProvidersForBooking = (booking) => {
    const bookingTerms = getBookingServiceTerms(booking);
    const maxDistanceValue = Number(maxDistance);
    const bookingCoords = extractCoords(booking);
    const hasBookingCoords = Number.isFinite(bookingCoords?.lat) && Number.isFinite(bookingCoords?.lng);

    return providers
      .filter((provider) => (
        provider.accountActive !== false &&
        provider.inactive !== true &&
        provider.accountStatus !== 'deactivated'
      ))
      .map((provider) => {
        const providerCoords = extractCoords(provider);
        const hasProviderCoords = Number.isFinite(providerCoords?.lat) && Number.isFinite(providerCoords?.lng);

        const distance = hasBookingCoords && hasProviderCoords
          ? calculateDistance(
            bookingCoords.lat,
            bookingCoords.lng,
            providerCoords.lat,
            providerCoords.lng
          )
          : null;

        const serviceDetails = getProviderServiceDetails(provider);
        const serviceMatch = providerMatchesBookingService(provider, bookingTerms);

        return {
          ...provider,
          distance,
          serviceDetails,
          serviceMatch,
        };
      })
      .filter((provider) => {
        if (maxDistanceValue > 0 && Number.isFinite(provider.distance)) {
          return provider.distance <= maxDistanceValue;
        }
        return true;
      })
      .sort((providerA, providerB) => {
        const aHasDistance = Number.isFinite(providerA.distance);
        const bHasDistance = Number.isFinite(providerB.distance);

        if (aHasDistance && bHasDistance) {
          return providerA.distance - providerB.distance;
        }
        if (aHasDistance) return -1;
        if (bHasDistance) return 1;

        if (providerA.serviceMatch !== providerB.serviceMatch) {
          return providerA.serviceMatch ? -1 : 1;
        }

        return getProviderDisplayName(providerA)
          .toLowerCase()
          .localeCompare(getProviderDisplayName(providerB).toLowerCase());
      });
  };

  const getNearestProvider = (booking) => {
    const providersForBooking = getProvidersForBooking(booking);
    const matchingProviders = providersForBooking.filter((provider) => provider.serviceMatch);
    return matchingProviders[0] || providersForBooking[0] || null;
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      // Load bookings with status 'lead' or 'rejected' or 'pending_provider'
      const bookingsCol = collection(db, 'bookings');
      const q = query(bookingsCol, where('status', 'in', ['lead', 'rejected', 'pending_provider']));
      const snap = await getDocs(q);
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime; // newest first
      });

      // Backfill booking coordinates from customer profile when booking doc has no coords.
      const customerIdsToLookup = Array.from(
        new Set(
          list
            .filter((booking) => !extractCoords(booking) && booking.customerId)
            .map((booking) => booking.customerId)
        )
      );

      if (customerIdsToLookup.length > 0) {
        const customerCoordsPairs = await Promise.all(
          customerIdsToLookup.map(async (customerId) => {
            try {
              const customerSnap = await getDoc(doc(db, 'users', customerId));
              if (!customerSnap.exists()) return [customerId, null];
              const coords = extractCoords(customerSnap.data());
              return [customerId, coords];
            } catch (lookupError) {
              console.warn('Failed to read customer profile for coords:', customerId, lookupError);
              return [customerId, null];
            }
          })
        );

        const customerCoordsMap = Object.fromEntries(
          customerCoordsPairs.filter(([, coords]) => Number.isFinite(coords?.lat) && Number.isFinite(coords?.lng))
        );

        list = list.map((booking) => {
          if (extractCoords(booking)) return booking;
          const fallbackCoords = customerCoordsMap[booking.customerId];
          return fallbackCoords
            ? { ...booking, customerCoords: fallbackCoords }
            : booking;
        });
      }

      setBookings(list);
      setFilteredBookings(list);

      // Load providers (client role = service provider)
      // Query for users with roles.client = true (service providers)
      const usersCol = collection(db, 'users');
      const pQ = query(usersCol, where('roles.client', '==', true));
      const pSnap = await getDocs(pQ);
      let pList = pSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Filter only approved providers
      pList = pList.filter(p => p.approvalStatus === 'approved' || p.isApproved === true);

      // Backfill provider coordinates from onboarding details when user profile has no coords.
      pList = await Promise.all(
        pList.map(async (provider) => {
          if (extractCoords(provider)) return provider;

          try {
            const onboardingSnap = await getDoc(doc(db, 'users', provider.id, 'details', 'provider_onboarding'));
            if (!onboardingSnap.exists()) return provider;

            const onboardingData = onboardingSnap.data();
            const onboardingCoords = extractCoords(onboardingData);

            return {
              ...provider,
              provider_onboarding: onboardingData,
              ...(onboardingCoords ? { providerLocation: onboardingCoords } : {}),
            };
          } catch (lookupError) {
            console.warn('Failed to read provider onboarding for coords:', provider.id, lookupError);
            return provider;
          }
        })
      );
      
      // Sort by name
      pList.sort((a, b) => {
        const nameA = (a.displayName || a.name || a.email || '').toLowerCase();
        const nameB = (b.displayName || b.name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setProviders(pList);
    } catch (err) {
      console.error('Error loading admin reassign bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    let filtered = bookings;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        b.customerName?.toLowerCase().includes(term) ||
        b.customerEmail?.toLowerCase().includes(term) ||
        b.serviceName?.toLowerCase().includes(term) ||
        b.subcategory?.toLowerCase().includes(term) ||
        b.id?.toLowerCase().includes(term)
      );
    }

    setFilteredBookings(filtered);
  }, [searchTerm, statusFilter, bookings]);

  const handleSelect = (bookingId, providerId) => {
    setSelectedMap(prev => ({ ...prev, [bookingId]: providerId }));
  };

  const handleAssign = async (bookingId, preferredProviderId = null) => {
    const booking = bookings.find((item) => item.id === bookingId);
    const nearestProvider = booking ? getNearestProvider(booking) : null;
    const providerId = preferredProviderId || selectedMap[bookingId] || nearestProvider?.id;

    if (!providerId) {
      alert('No provider available for assignment within the current filters.');
      return;
    }

    setAssigning(prev => ({ ...prev, [bookingId]: true }));
    try {
      const resp = await fetch(`${CLOUD_FUNCTION_BASE}/assignBookingToProvider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId, providerId }),
      });
      const json = await resp.json();
      if (json?.success) {
        // Remove from list
        setBookings(prev => prev.filter(b => b.id !== bookingId));
        setSelectedMap(prev => {
          const { [bookingId]: _, ...rest } = prev;
          return rest;
        });
      } else {
        console.error('Assign failed', json);
        alert('Assign failed: ' + (json?.error || JSON.stringify(json)));
      }
    } catch (err) {
      console.error('Assign error', err);
      alert('Assign error: ' + err.message);
    } finally {
      setAssigning(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return 'N/A';
    return timestamp.toDate().toLocaleDateString() + ' ' + timestamp.toDate().toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'rejected': return 'error';
      case 'lead': return 'warning';
      case 'pending_provider': return 'info';
      default: return 'default';
    }
  };

  if (loading) return <Box p={3}><CircularProgress /></Box>;

  return (
    <Box p={1}>
      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Open Pool / Pending Bookings</Typography>
        <IconButton onClick={loadBookings} color="primary" title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box> */}

      {/* Filters */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            placeholder="Search by customer, service, or booking ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="pending_provider">Pending Provider</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Max Distance</InputLabel>
            <Select
              value={maxDistance}
              label="Max Distance"
              onChange={(e) => setMaxDistance(Number(e.target.value))}
            >
              <MenuItem value={0}>All Providers</MenuItem>
              <MenuItem value={5}>Within 5 km</MenuItem>
              <MenuItem value={10}>Within 10 km</MenuItem>
              <MenuItem value={20}>Within 20 km</MenuItem>
              <MenuItem value={30}>Within 30 km</MenuItem>
              <MenuItem value={50}>Within 50 km</MenuItem>
              <MenuItem value={100}>Within 100 km</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredBookings.length} of {bookings.length} bookings
            {providers.length > 0 && ` • ${providers.length} approved providers available`}
          </Typography>
        </Box>
      </Card>

      {/* Bookings Grid */}
      {filteredBookings.length === 0 ? (
        <Alert severity="info">
          {bookings.length === 0 
            ? 'No bookings pending reassignment.' 
            : 'No bookings match your search criteria.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredBookings.map((b) => {
            const providersForBooking = getProvidersForBooking(b);
            const matchingProviders = providersForBooking.filter((provider) => provider.serviceMatch);
            const nearestProvider = matchingProviders[0] || providersForBooking[0] || null;
            const selectedProvider = providersForBooking.find((provider) => provider.id === selectedMap[b.id]);

            return (
              <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={b.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {b.serviceName || b.subcategory || 'Service'}
                      </Typography>
                      <Chip
                        label={b.status}
                        color={getStatusColor(b.status)}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {b.customerName || 'Unknown Customer'}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                        {b.customerEmail || b.customerPhone || 'No contact info'}
                      </Typography>

                      {b.location && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" noWrap>
                            {b.location}
                          </Typography>
                        </Box>
                      )}

                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(b.createdAt)}
                        </Typography>
                      </Box>

                      {(b.totalAmount || b.price) && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>
                            ${b.totalAmount || b.price}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        Requested: {[b.category || b.leadCategory, b.subcategory || b.leadSubcategory].filter(Boolean).join(' / ') || 'General'}
                      </Typography>

                      {b.rejectionReason && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            <strong>Rejection reason:</strong> {b.rejectionReason}
                          </Typography>
                        </Alert>
                      )}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" gap={1} flexDirection="column">
                      {nearestProvider ? (
                        <Typography variant="caption" color="success.main">
                          Nearest available: {getProviderDisplayName(nearestProvider)}
                          {` (${formatDistanceLabel(nearestProvider.distance)})`}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="error.main">
                          No provider is available within the selected distance.
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {providersForBooking.length} providers in range
                        {matchingProviders.length > 0 ? `, ${matchingProviders.length} service-matched` : ''}
                      </Typography>

                      <FormControl fullWidth size="small">
                        <InputLabel id={`select-${b.id}`}>Assign Provider</InputLabel>
                        <Select
                          labelId={`select-${b.id}`}
                          value={selectedMap[b.id] || ''}
                          label="Assign Provider"
                          onChange={(e) => handleSelect(b.id, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Select provider...</em>
                          </MenuItem>
                          {providersForBooking.map((provider) => (
                            <MenuItem key={provider.id} value={provider.id}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {getProviderDisplayName(provider)}
                                  {` (${formatDistanceLabel(provider.distance)})`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={provider.serviceMatch ? 'success.main' : 'text.secondary'}
                                  noWrap
                                >
                                  Services: {provider.serviceDetails.slice(0, 3).join(', ') || 'Not listed'}
                                  {!provider.serviceMatch ? ' | no exact service match' : ''}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedProvider && (
                        <Typography variant="caption" color="text.secondary">
                          Selected provider services: {selectedProvider.serviceDetails.join(', ') || 'Not listed'}
                        </Typography>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => handleAssign(b.id, nearestProvider?.id || null)}
                          disabled={assigning[b.id] || !nearestProvider}
                        >
                          {assigning[b.id] ? 'Assigning...' : 'Assign Nearest'}
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleAssign(b.id)}
                          disabled={assigning[b.id] || (!selectedMap[b.id] && !nearestProvider)}
                        >
                          {assigning[b.id] ? 'Assigning...' : 'Assign Selected'}
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
